import React from "react";
import gql from "graphql-tag";
import { graphql } from "react-apollo";
import * as R from "ramda";
import { Tunnel } from "react-tunnels";
import { css } from "glamor";
import { Alert, Input, Row, Col } from "antd";

import { DEFAULT_PAGESIZE } from "../../config";
import { debounce } from "../../util";

import ApolloPaginationTable from "../../container/ApolloPaginationTable";
import getAdvancedSearchColumns from "./AdvancedSearchColumns";
import AdvancedSearchForm from "./AdvancedSearchForm";
import ActionMenu from "./ActionMenu";

const SEARCH_ENTITIES = gql`
    query SearchEntitiesQuery(
        $name: String
        $lookupAlternativeNames: Boolean
        $risks: [String]
        $ulid: String
        $monitoringLevel: String
        $entitySource: String
        $frequency: String
        $entityType: [String]
        $ownershipType: String
        $state: String
        $registrationStatus: [String]
        $countries: [String]
        $externalId: String
        $identificationNumber: String
        $linesOfBusiness: [String]
        $count: Int!
        $cursor: String
    ) {
        searchEntities(
            name: $name
            lookupAlternativeNames: $lookupAlternativeNames
            riskLevels: $risks
            ulid: $ulid
            monitoringLevel: $monitoringLevel
            entitySource: $entitySource
            frequency: $frequency
            entityType: $entityType
            ownershipType: $ownershipType
            state: $state
            registrationStatus: $registrationStatus
            countries: $countries
            externalId: $externalId
            identificationNumber: $identificationNumber
            linesOfBusiness: $linesOfBusiness
            first: $count
            after: $cursor
        )
            @connection(
                key: "SearchEntitiesQuery_entities"
                filter: [
                    "name"
                    "riskLevels"
                    "ulid"
                    "monitoringLevel"
                    "entitySource"
                    "frequency"
                    "entityType"
                    "ownershipType"
                    "state"
                    "registrationStatus"
                    "countries"
                    "externalId"
                    "identificationNumber"
                    "linesOfBusiness"
                ]
            ) {
            edges {
                node {
                    id
                    key {
                        ulid
                    }
                    name
                    entityType
                    activeProcesses
                    legalAddress {
                        countryCode
                    }
                    physicalAddress {
                        countryCode
                    }
                    createdDate
                    additionalProperties
                    monitoring
                    linesOfBusiness
                }
            }
            pageInfo {
                endCursor
                hasNextPage
                totalCount
            }
            facets {
                fieldName
                values {
                    label
                    value
                    count
                }
            }
        }
    }
`;

const styles = {
    searchInput: css({
        width: "350px",
    }),
    advancedSearchToggle: css({
        fontStyle: "italic",
        textAlign: "right",
    }),
    errorMessage: css({
        marginBottom: "1rem",
    }),
};

class EntityStore extends React.Component {
    state = {
        isAdvancedSearch: false,
        filters: {},
    };

    _facets = [];
    columns = getAdvancedSearchColumns("node");

    toggleAdvancedSearch = e => {
        e.preventDefault();
        this.setState(state => ({ isAdvancedSearch: !state.isAdvancedSearch }));
    };

    getFacets = () => {
        // We are caching the facets here, and assuming that this is MOST of the
        // facets we can get assuming that we have no filters applied.
        // This is so that we can display all possible options for fields like
        // "Countries". If we simply used the facets available from the search response,
        // a multi-select field like `Countries` will not be very useful as we cannot
        // select more than one country before the list is limited.
        if (R.isEmpty(this._facets)) {
            this._facets = R.pathOr(
                [],
                ["data", "searchEntities", "facets"],
                this.props
            );
        }
        return this._facets;
    };

    handleFilter = key => e => {
        // This is to handle events from input fields,
        // as well as select, and radio fields.
        // Unfortunately, the underlying Ant Design form components
        // behave differently.
        const value = e && e.target ? e.target.value : e;
        this.setFilters({
            ...this.state.filters,
            [key]: value,
        });
    };

    handleClearFilters = () => {
        // "Nullify" the existing filters.
        // If we simply set the filters to `{}`, it will not override
        // the existing ones.
        this.setFilters(R.map(R.empty, this.state.filters));
    };

    setFilters = (filters = {}) => {
        this.setState({ filters }, () => {
            this._applyWithDebounce(
                this.props.data.refetch,
                this.state.filters
            );
        });
    };

    parseError = error => {
        if (!error) {
            return;
        } else if (error.toString().includes("Failed to parse query")) {
            return "The search query entered is not valid. Please try again.";
        } else {
            return "Sorry, an unexpected error has occurred on our side, and we are looking into it.";
        }
    };

    _applyWithDebounce = debounce((fn, args) => {
        fn(args);
    }, 500);

    render() {
        const { filters } = this.state;
        const locationKey = R.pathOr("", ["location", "key"], this.props);

        const error = this.parseError(this.props.data.error);

        return (
            <div>
                <Tunnel id="bluebar-left-content">
                    <div>
                        {/*
                            `locationKey` is used to "refresh" the search input
                            when we navigate to the same page or a different page.
                            Otherwise, the list of entities may "refresh", but this
                            uncontrolled component will not, and it will not be
                            reflective of the true filters.
                        */}
                        <Input.Search
                            {...styles.searchInput}
                            data-at-id="entity-search-input"
                            key={locationKey}
                            placeholder="Entity Name"
                            size="large"
                            onChange={this.handleFilter("name")}
                            value={filters.name}
                        />
                    </div>
                    <div {...styles.advancedSearchToggle}>
                        <a onClick={this.toggleAdvancedSearch}>
                            {this.state.isAdvancedSearch
                                ? "Simple Search"
                                : "Advanced Search"}
                        </a>
                    </div>
                </Tunnel>

                <Tunnel id="bluebar-right-content">
                    <ActionMenu key={locationKey} />
                </Tunnel>

                <Row gutter={8}>
                    <Col span={this.state.isAdvancedSearch ? 5 : 0}>
                        <AdvancedSearchForm
                            onFilterChange={this.handleFilter}
                            onClearFilters={this.handleClearFilters}
                            filters={filters}
                            facets={this.getFacets()}
                        />
                    </Col>
                    <Col span={this.state.isAdvancedSearch ? 19 : 24}>
                        {error && (
                            <Alert
                                {...styles.errorMessage}
                                message={error}
                                type="warning"
                                closable
                            />
                        )}

                        <ApolloPaginationTable
                            // NOTE: this structure is odd because `ApolloPaginationTable`
                            // was originally built to support the props returned
                            // from `<Query>` instead of `graphql` HOC.
                            // More work can be done in `ApolloPaginationTable` to
                            // handle both cases (e.g. looking up `__typename`).
                            apolloProps={{
                                ...this.props.data,
                                data: {
                                    searchEntities: this.props.data
                                        .searchEntities,
                                },
                            }}
                            columns={this.columns}
                        />
                    </Col>
                </Row>
            </div>
        );
    }
}

export default graphql(SEARCH_ENTITIES, {
    options: {
        // Display as quickly as possible, but update with the latest data
        fetchPolicy: "cache-and-network",
        variables: {
            name: "",
            count: DEFAULT_PAGESIZE,
            cursor: null,
        },
    },
})(EntityStore);
